import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';
import { redactError } from '../utils/redactError';

const logger = createLogger('waterSewerProvider');
export interface WaterSewerProviderInfo {
  id: string;
  userId: string;
  companyName?: string;
  serviceType?: string;
  accountNumber?: string;
  serviceAddress?: string;
  billingAddress?: string;
  customerServicePhone?: string;
  emergencyPhone?: string;
  website?: string;
  billingCycle?: string;
  monthlyCost?: number;
  autopayEnabled?: boolean;
  autopayMethod?: string;
  meterNumber?: string;
  shutoffLocation?: string;
  notes?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

class WaterSewerProviderInfoService {
  private baseURL = `${API_URL}/api/water-sewer-provider-info`;

  private async getHeaders() {
    const token = await getAuthToken();
    logger.debug('[Service Debug] Retrieved token from AsyncStorage:', token ? `[TOKEN: ${token.substring(0, 20)}...]` : '[NO TOKEN FOUND]');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  async getAllWaterSewerProviderInfo(targetUserId?: string): Promise<WaterSewerProviderInfo[]> {
    try {
      logger.debug('[Service Debug] Starting getAllWaterSewerProviderInfo API call');
      logger.debug('[Service Debug] Target User ID:', targetUserId);
      const headers = await this.getHeaders();
      const url = targetUserId ? `${this.baseURL}?targetUserId=${targetUserId}` : this.baseURL;
      logger.debug('[Service Debug] Headers prepared:', { ...headers, Authorization: headers.Authorization ? '[TOKEN PRESENT]' : '[NO TOKEN]' });
      logger.debug('[Service Debug] Making request to:', url);

      const response = await axios.get(url, { headers });
      logger.debug('[Service Debug] API response status:', response.status);
      logger.debug('[Service Debug] API response data:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch water/sewer provider information');
      }
      
      return response.data.data || [];
    } catch (error) {
      logger.error('[Service Debug] Error fetching water/sewer provider info:', redactError(error));
      logger.error('[Service Debug] Error status:', error.response?.status);
      throw error;
    }
  }

  async getWaterSewerProviderInfoById(id: string): Promise<WaterSewerProviderInfo> {
    try {
      logger.debug('[Service Debug] Fetching water/sewer provider by ID:', id);
      const headers = await this.getHeaders();
      const response = await axios.get(`${this.baseURL}/${id}`, { headers });
      logger.debug('[Service Debug] getById API response:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch water/sewer provider information');
      }
      
      return response.data.data;
    } catch (error) {
      logger.error('[Service Debug] Error fetching water/sewer provider info by ID:', redactError(error));
      throw error;
    }
  }

  async createWaterSewerProviderInfo(data: Partial<WaterSewerProviderInfo>): Promise<WaterSewerProviderInfo> {
    try {
      const headers = await this.getHeaders();
      const response = await axios.post(this.baseURL, data, { headers });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create water/sewer provider information');
      }
      
      return response.data.data;
    } catch (error) {
      logger.error('Error creating water/sewer provider info:', redactError(error));
      throw error;
    }
  }

  async updateWaterSewerProviderInfo(id: string, data: Partial<WaterSewerProviderInfo>): Promise<WaterSewerProviderInfo> {
    try {
      const headers = await this.getHeaders();
      const response = await axios.patch(`${this.baseURL}/${id}`, data, { headers });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update water/sewer provider information');
      }
      
      return response.data.data;
    } catch (error) {
      logger.error('Error updating water/sewer provider info:', redactError(error));
      throw error;
    }
  }

  async deleteWaterSewerProviderInfo(id: string): Promise<void> {
    try {
      const headers = await this.getHeaders();
      await axios.delete(`${this.baseURL}/${id}`, { headers });
    } catch (error) {
      logger.error('Error deleting water/sewer provider info:', redactError(error));
      throw error;
    }
  }

  async lockWaterSewerProviderInfo(id: string, grantedEmails: string[] = []): Promise<void> {
    try {
      const headers = await this.getHeaders();
      await axios.post(`${this.baseURL}/${id}/lock`, { granteeEmails: grantedEmails }, { headers });
    } catch (error) {
      logger.error('Error locking water/sewer provider info:', redactError(error));
      throw error;
    }
  }

  async unlockWaterSewerProviderInfo(id: string, grantedEmails: string[]): Promise<void> {
    try {
      const headers = await this.getHeaders();
      await axios.post(`${this.baseURL}/${id}/unlock`, { granteeEmails: grantedEmails }, { headers });
    } catch (error) {
      logger.error('Error unlocking water/sewer provider info:', redactError(error));
      throw error;
    }
  }

  async getWaterSewerProviderAccessGrants(id: string): Promise<any[]> {
    try {
      const headers = await this.getHeaders();
      const response = await axios.get(`${this.baseURL}/${id}/access-grants`, { headers });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch water/sewer provider access grants');
      }
      
      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching water/sewer provider access grants:', redactError(error));
      throw error;
    }
  }
}

export const waterSewerProviderInfoService = new WaterSewerProviderInfoService();